import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { generateClient } from "aws-amplify/data";
import { wordsByWord, wordsByTranslation } from "../../graphql/queries";
import translateText from "../../utils/azureTranslate";
import detectLanguage from "../../utils/detectLanguage";
import { createGlobalVocabulary } from "../../graphql/mutations";
import generateSpeech from "../../utils/azureTextToSpeech";

// Generate an Amplify client to interact with the GraphQL API
const client = generateClient();

export const fetchWords = createAsyncThunk(
  "search/fetchWords", // Action type string
  async ({ term, triggerTranslate }, thunkAPI) => {
    try {
      let isSearchByWord = true; // Flag to determine if the search was by word

      // Execute a GraphQL query to search for words by the provided term
      const wordResponse = await client.graphql({
        query: wordsByWord, // GraphQL query to search by word
        variables: { word: term, limit: 5 }, // Limit the results to 5 items
      });

      // Extract the word results from the response
      const wordResults = wordResponse.data.wordsByWord?.items || [];
      let combinedResults = wordResults;

      // If no word results are found, search by translation
      if (wordResults.length === 0) {
        const translationResponse = await client.graphql({
          query: wordsByTranslation, // GraphQL query to search by translation
          variables: { translation: term, limit: 5 }, // Limit the results to 5 items
        });

        // Extract the translation results from the response
        const translationResults =
          translationResponse.data.wordsByTranslation?.items || [];

        combinedResults = translationResults; // Combine results with translation results
        isSearchByWord = false; // Indicate that the search was by translation
      }

      // Only fallback to Azure Translate if triggerTranslate is true and no results are found
      if (combinedResults.length === 0 && triggerTranslate) {
        // Detect the language using the Azure API
        const detectedLanguage = await detectLanguage(term);

        let newWord = null;

        // Handle translation based on detected language
        if (detectedLanguage === "ru") {
          // If the detected language is Russian, translate to Serbian
          let translation = await translateText(term, "sr");
          newWord = {
            word: translation,
            translation: term,
          };
        } else {
          // Translate any other language to Russian
          let translation = await translateText(term, "ru");
          let speech = await generateSpeech(term);
          newWord = {
            word: term,
            translation,
            textToSpeech: speech,
          };
        }

        // Save the translated word to the global vocabulary
        await client.graphql({
          query: createGlobalVocabulary, // GraphQL mutation to create a new vocabulary entry
          variables: { input: newWord }, // Input variables for the mutation
        });

        combinedResults = [newWord]; // Set combined results to the newly created word
        isSearchByWord = true; // Assume we're searching by word when using Azure
      }

      // Return the search results and the search type flag
      return {
        results: combinedResults,
        isSearchByWord,
      };
    } catch (error) {
      // Handle errors by rejecting the thunk with an error message
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Create a Redux slice for handling search-related state
const searchSlice = createSlice({
  name: "search", // Name of the slice
  initialState: {
    searchTerm: "", // The current search term
    results: [], // Array to store the search results
    loading: false, // Flag to indicate if the search is in progress
    error: null, // Error message, if any
    isSearchByWord: true, // Track whether the search was by word or by translation
    triggerTranslate: false, // Track whether to trigger Azure Translate
  },
  reducers: {
    // Reducer to set the current search term
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    },
    // Reducer to clear the search results
    clearResults: (state) => {
      state.results = [];
    },
    // Reducer to set whether to trigger Azure Translate
    setTriggerTranslate: (state, action) => {
      state.triggerTranslate = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Handle the different states of the asynchronous thunk action
    builder
      .addCase(fetchWords.pending, (state) => {
        state.loading = true; // Set loading to true when the action is pending
      })
      .addCase(fetchWords.fulfilled, (state, action) => {
        state.loading = false; // Set loading to false when the action is fulfilled
        state.results = action.payload.results; // Store the results from the action payload
        state.isSearchByWord = action.payload.isSearchByWord; // Update the search type flag
      })
      .addCase(fetchWords.rejected, (state, action) => {
        state.loading = false; // Set loading to false when the action is rejected
        state.error = action.payload; // Capture the error message
      });
  },
});

export const { setSearchTerm, clearResults, setTriggerTranslate } =
  searchSlice.actions;

export default searchSlice.reducer;
